import {
  restoreErrorMessage,
  useRestoreCanvas,
  useRestoreFlowSession,
  useRestoreWorkspace,
} from "@/api/archived";
import { useFlowSessionById } from "@/api/flow_sessions";
import { useFlowMessages } from "@/api/messages";
import ChatArea from "@/components/ChatArea";
import { InTrashBanner } from "@/components/InTrashBanner/InTrashBanner";
import { MessageInput } from "@/components/MessageInput";
import { ReferencedEntitiesPills } from "@/components/ReferencedEntitiesPills";
import { MessageInputControlSet } from "@/constants/message-input";
import { MessageInputProvider } from "@/context/MessageInputContext";
import { SessionProvider } from "@/context/SessionContext";
import { ChatComposerDropTarget } from "@/dnd/ChatComposerDropTarget";
import { useOnDrop } from "@/dnd/OsFileDropLayer";
import { useRegisterChatAttachHandler } from "@/dnd/TransferDndProvider";
import { useMessageActions } from "@/hooks/useMessageActions";
import { useReasoningEffort } from "@/pages/canvas/hooks/useCanvasReasoningEffort";
import { useFileRecordTextSelectionReferencedEntity } from "@/panes/file-record-detail/useFileRecordTextSelectionReferencedEntity";
import type { PostMessageParams } from "@cohesive/api-client";
import { SuggestionPills } from "@cohesive/ui-components";
import {
  FileText,
  HelpCircle,
  Lightbulb,
  SearchIcon,
  Sparkles,
} from "lucide-react";
import { motion } from "motion/react";
import { useCallback } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

interface SuggestedAction {
  id: string;
  label: string;
  message: string;
  icon: React.ComponentType<{ className?: string }>;
}

const SUGGESTED_ACTIONS: SuggestedAction[] = [
  {
    id: "summarize-files",
    label: "Summarize my files",
    message: "Summarize the files in this workspace for me.",
    icon: SearchIcon,
  },
  {
    id: "brainstorm",
    label: "Brainstorm ideas",
    message: "Help me brainstorm ideas for a new project in this workspace.",
    icon: Lightbulb,
  },
  {
    id: "create-doc",
    label: "Create a document",
    message: "Create a new document for me.",
    icon: FileText,
  },
  {
    id: "help",
    label: "What can you do?",
    message: "What are all the things you can help me with here?",
    icon: HelpCircle,
  },
];

function WorkspaceChatBody({ flowSessionId }: { flowSessionId: string }) {
  const {
    onSendMessage: onSendMessageBase,
    isLoading,
    uploadedFiles,
    onAttachFile,
    onAttachFileRecords,
    onRemoveFile,
  } = useMessageActions({});

  // Text excerpts pushed from a right-pane HTML/MD file-record viewer ride
  // along on the next message, scoped to this chat's flowSessionId (mirrors the
  // canvas chat, which scopes the same selections by canvasId).
  const { textSelectionEntities, removeSelection, clearSelectionsFromMessage } =
    useFileRecordTextSelectionReferencedEntity(flowSessionId);

  // Drop the text-selection pills once they've ridden along on a sent message.
  const onSendMessage = useCallback(
    async (params: PostMessageParams) => {
      await onSendMessageBase(params);
      clearSelectionsFromMessage(params.referenced_entities);
    },
    [onSendMessageBase, clearSelectionsFromMessage],
  );

  useOnDrop(onAttachFile);
  useRegisterChatAttachHandler(onAttachFileRecords);

  const { data: messages = [], isPending: isMessagesPending } = useFlowMessages(
    flowSessionId,
    undefined,
    { enabled: !!flowSessionId },
  );

  const [reasoningEffort, setReasoningEffort] = useReasoningEffort(
    `chat_reasoning_effort_${flowSessionId}`,
  );

  const hasMessages = messages.length > 0;

  const handleSuggestedAction = useCallback(
    (action: SuggestedAction) => {
      onSendMessage({
        id: uuidv4(),
        content: action.message,
        referenced_entities: textSelectionEntities,
      } as PostMessageParams);
    },
    [onSendMessage, textSelectionEntities],
  );

  // IMPORTANT: MessageInput is rendered unconditionally at the bottom so that
  // AudioRecordingButton / useAudioRecording never unmounts while a recording
  // is active (mirrors the canvas session pane pattern). ChatArea mounts above
  // with hideMessageInput=true when there are messages, so the stable input
  // below remains the sole composer and controls send/stop.
  return (
    <ChatComposerDropTarget
      flowSessionId={flowSessionId}
      className="flex flex-1 min-h-0 flex-col overflow-hidden"
    >
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden px-4">
        {isMessagesPending ? (
          <div className="flex-1 min-h-0" aria-busy />
        ) : hasMessages ? (
          <div className="flex-1 min-h-0 overflow-hidden">
            <ChatArea
              location="main"
              controlSet={MessageInputControlSet.WORKSPACE_CHAT}
              placeholder="Ask Cohesive anything…"
              className="h-full"
              hideMessageInput={true}
              referencedEntities={textSelectionEntities}
              onSendSuccess={clearSelectionsFromMessage}
            />
          </div>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center p-4 overflow-y-auto">
            <div className="max-w-measure text-center font-sans text-md text-n-60">
              <p className="flex items-center justify-center gap-2">
                <Sparkles className="size-4 text-rust" /> What should we get
                done?
              </p>
              <p className="mt-1 text-xs text-n-50">
                Mention a file with @, or run a Skill with the spark.
              </p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="mt-6 max-w-md"
            >
              <SuggestionPills
                className="justify-center"
                suggestions={SUGGESTED_ACTIONS.map((action) => ({
                  label: action.label,
                  icon: action.icon,
                }))}
                onSelect={(suggestion) => {
                  const action = SUGGESTED_ACTIONS.find(
                    (a) => a.label === suggestion.label,
                  );
                  if (action) handleSuggestedAction(action);
                }}
              />
            </motion.div>
          </div>
        )}

        <div className="shrink-0 pb-6">
          <MessageInput
            flowSessionId={flowSessionId}
            onSend={onSendMessage}
            attachedFiles={uploadedFiles}
            onAttachFile={onAttachFile}
            onRemoveFile={onRemoveFile}
            sendButtonDisabled={isLoading}
            controlSet={MessageInputControlSet.WORKSPACE_CHAT}
            location="main"
            placeholder="Ask Cohesive anything…"
            autoFocus={!isMessagesPending && !hasMessages}
            agentReasoningEffort={reasoningEffort}
            onAgentReasoningEffortChange={setReasoningEffort}
            referencedEntities={textSelectionEntities}
            middleToolbarSlot={
              textSelectionEntities.length > 0 ? (
                <ReferencedEntitiesPills
                  entities={textSelectionEntities}
                  onRemove={removeSelection}
                  className="px-0 py-0"
                />
              ) : null
            }
            classNames={{
              container: "w-full sm:max-w-[776px] mx-auto",
            }}
          />
        </div>
      </div>
    </ChatComposerDropTarget>
  );
}

function WorkspaceChatPageInner({ flowSessionId }: { flowSessionId: string }) {
  const { data: session } = useFlowSessionById(flowSessionId);
  const restoreChat = useRestoreFlowSession({
    onSuccess: () => toast.success("Restored chat."),
    onError: (err) =>
      toast.error(restoreErrorMessage(err, "Failed to restore chat")),
  });
  const restoreCanvas = useRestoreCanvas({
    onSuccess: () => toast.success("Restored Canvas."),
    onError: (err) =>
      toast.error(restoreErrorMessage(err, "Failed to restore Canvas")),
  });
  const restoreWorkspace = useRestoreWorkspace({
    onSuccess: () => toast.success("Restored Workspace."),
    onError: (err) =>
      toast.error(restoreErrorMessage(err, "Failed to restore Workspace")),
  });

  if (session?.archived_at) {
    const archiveRootId = session.archived_with_resource_id;
    const parentKind = archiveRootId
      ? archiveRootId === session.canvas_id
        ? ("canvas" as const)
        : ("workspace" as const)
      : undefined;

    return (
      <div className="relative flex flex-col h-full bg-surface-main">
        <div className="mx-auto w-full max-w-[720px] px-4 pt-6">
          <InTrashBanner
            kind="chat"
            parentKind={parentKind}
            isRestoring={
              restoreChat.isPending ||
              restoreCanvas.isPending ||
              restoreWorkspace.isPending
            }
            onRestore={() => {
              if (archiveRootId && parentKind === "canvas") {
                restoreCanvas.mutate(archiveRootId);
              } else if (archiveRootId) {
                restoreWorkspace.mutate(archiveRootId);
              } else {
                restoreChat.mutate(flowSessionId);
              }
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col h-full min-h-0 bg-surface-main">
      <SessionProvider initialSessionId={flowSessionId}>
        <WorkspaceChatBody flowSessionId={flowSessionId} />
      </SessionProvider>
    </div>
  );
}

export function WorkspaceChatPage() {
  const { flowSessionId } = useParams<{ flowSessionId: string }>();

  if (!flowSessionId) {
    return null;
  }

  return (
    <MessageInputProvider>
      <WorkspaceChatPageInner flowSessionId={flowSessionId} />
    </MessageInputProvider>
  );
}

export default WorkspaceChatPage;
